/**
 * Scene Document types — re-exported from Orval-generated DTOs with clean aliases.
 * Import from here (not from generated models directly) so the rest of the
 * codebase stays decoupled from Orval naming conventions.
 */

// ─── Core types ────────────────────────────────────────────────────────────

export type { SceneDocumentDto as SceneDocument } from '@/apis/backend/generated/models';
export type { SceneNodeDto as SceneNode } from '@/apis/backend/generated/models';
export type { SceneNodeDtoFillsItem as Paint } from '@/apis/backend/generated/models';
export type { ImageTransformDto as ImageTransform } from '@/apis/backend/generated/models';
export type { ImageAdjustmentsDto as ImageAdjustments } from '@/apis/backend/generated/models';
export type { DuotoneAdjustmentDto as DuotoneAdjustment } from '@/apis/backend/generated/models';
export type { ScrimDto as Scrim } from '@/apis/backend/generated/models';

// ─── Paint sub-types ───────────────────────────────────────────────────────

export type { SolidPaintDto as SolidPaint } from '@/apis/backend/generated/models';
export type { LinearGradientPaintDto as LinearGradientPaint } from '@/apis/backend/generated/models';
export type { RadialGradientPaintDto as RadialGradientPaint } from '@/apis/backend/generated/models';
export type { ImagePaintDto as ImagePaint } from '@/apis/backend/generated/models';
export type { GradientStopDto as GradientStop } from '@/apis/backend/generated/models';
export type { EffectDto as Effect } from '@/apis/backend/generated/models';

// ─── Enum types ────────────────────────────────────────────────────────────

export type { SceneNodeDtoType as SceneNodeType } from '@/apis/backend/generated/models';
export type { SceneNodeDtoSlotRole as SlotRole } from '@/apis/backend/generated/models';
export type { SceneNodeDtoBlendMode as BlendMode } from '@/apis/backend/generated/models';
export type { SceneNodeDtoStrokeAlign as StrokeAlign } from '@/apis/backend/generated/models';
export type { SceneNodeDtoStrokeJoin as StrokeJoin } from '@/apis/backend/generated/models';
export type { SceneNodeDtoStrokeCap as StrokeCap } from '@/apis/backend/generated/models';

// ─── Runtime enum values (for validation, iteration, etc.) ─────────────────

export { SceneNodeDtoSlotRole as SLOT_ROLES } from '@/apis/backend/generated/models';
export { SceneNodeDtoType as SCENE_NODE_TYPES } from '@/apis/backend/generated/models';
export { SceneNodeDtoBlendMode as BLEND_MODES } from '@/apis/backend/generated/models';

// ─── Helpers ───────────────────────────────────────────────────────────────

import type { SceneDocumentDto } from '@/apis/backend/generated/models';

export function isSceneDocument(data: unknown): data is SceneDocumentDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'version' in data &&
    (data as Record<string, unknown>).version === 2
  );
}

/** Repairs legacy docs that were saved without `rootChildren` or with `nodes` as an array. */
export function normalizeSceneDocument(doc: SceneDocumentDto): SceneDocumentDto {
  const nodes: SceneDocumentDto['nodes'] =
    Array.isArray(doc.nodes) ? {} : (doc.nodes ?? {});
  const rootChildren: string[] =
    Array.isArray(doc.rootChildren) ? doc.rootChildren : [];
  return { ...doc, nodes, rootChildren };
}
